import React, { useEffect, useRef } from "react"
import { ReactSVG } from "react-svg"
import { CustomBlockInternalPageFieldsFragment } from "../../../../graphql-types"
import { pushGtmEvent } from "../../../gtm/interaction-event"
import { configureSVGAccessibility } from "../../../utils/accessibility"
import * as inputsStyles from "../../layout/inputs.module.scss"
import * as globalBlockStyles from "../main-blocks/main-blocks.module.scss"
import * as blockStyles from "../custom-block/custom-block.module.scss"
import * as styles from "./luas-next-trams-block.module.scss"

type RenderProps = {
  block: CustomBlockInternalPageFieldsFragment
  intersectionRatioCallback: (ratio: number) => void
  locale: string
  defaultLocale: string
}

export const isLuasNextTramsBlock = (block: {
  __typename?: string
  contentPageLink?: { slug?: string | null } | null
}): block is CustomBlockInternalPageFieldsFragment =>
  block.__typename === "DatoCmsCustomBlockInternalPage" && block.contentPageLink?.slug === "real-time-stop"

const LuasNextTramsBlock: React.FC<RenderProps> = ({ block, intersectionRatioCallback, locale, defaultLocale }) => {
  const component = useRef<HTMLElement>(null)
  const observer = useRef<IntersectionObserver>(null)
  const intersectionRatioCallbackRef = useRef(intersectionRatioCallback)

  useEffect(() => {
    intersectionRatioCallbackRef.current = intersectionRatioCallback
  }, [intersectionRatioCallback])

  useEffect(() => {
    if (!("IntersectionObserver" in window)) return
    const options = { threshold: [0.2, 0.4, 0.6, 0.8, 1.0] }
    observer.current = new IntersectionObserver(entries => {
      intersectionRatioCallbackRef.current(entries[entries.length - 1].intersectionRatio)
    }, options)
    observer.current.observe(component.current)
    return () => observer.current.disconnect()
  }, [])

  const localePrefix = locale === defaultLocale ? "" : `/${locale}`
  const action = `${localePrefix}/${block.contentPageLink.slug}/`

  return (
    <section className={`${blockStyles.block} ${globalBlockStyles.mainBlocksCard} ${styles.formCard}`} ref={component}>
      <h2 className={blockStyles.title}>{block.title}</h2>
      <div className={styles.iconContainer}>
        {block.image.format === "svg" ? (
          <ReactSVG
            src={block.image.url}
            className={styles.icon}
            beforeInjection={svg => configureSVGAccessibility(svg, block.image.alt || "")}
          />
        ) : (
          <img src={block.image.url} className={styles.icon} alt={block.image.alt || ""} />
        )}
      </div>
      <a
        aria-label={`${block.linkLabel || "Find"} - ${block.title}`}
        className={`${styles.searchButton} ${inputsStyles.primaryButton}`}
        href={action}
        onClick={() => pushGtmEvent("eventga", "Homepage", "Bloc Principal", block.linkLabel || "Find")}
      >
        {block.linkLabel || "Find"}
      </a>
    </section>
  )
}

export default LuasNextTramsBlock
